import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { listMedicalUnits } from "@/core/admin/establishment-store";
import {
  callNextTicket,
  completeQueueTicket,
  listQueueTickets,
  orientPaidTicketToClinical,
  startQueueTicket,
} from "@/core/queue/service-queue-store";
import {
  QUEUE_STATUS_LABELS,
  SERVICE_QUEUE_LABELS,
  type QueueTicket,
  type ServiceQueueId,
} from "@/core/queue/types";
import {
  createConsultation,
  consultationDetailPath,
  getPharmacyPrescriptionForPatient,
  pharmacyOrderPath,
} from "@/core/clinical/clinical-store";
import { createLabOrder } from "@/core/laboratory/lab-store";
import { createImagingExam } from "@/core/imaging/imaging-store";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { routes } from "@/router/all_routes";

interface ServiceQueuePanelProps {
  serviceId: ServiceQueueId;
  /** Filtre unité (ex. Médecine générale). Vide = toutes les unités du service. */
  defaultUnitName?: string;
  /** Afficher sélecteur box + bouton appeler. */
  enableCallNext?: boolean;
  title?: string;
  /** Filtre optionnel sur les tickets affichés. */
  ticketFilter?: (ticket: QueueTicket) => boolean;
}

const DEFAULT_BOXES = ["Box 1", "Box 2", "Box 3"];

export function ServiceQueuePanel({
  serviceId,
  defaultUnitName = "",
  enableCallNext = false,
  title,
  ticketFilter,
}: ServiceQueuePanelProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  const [unitName, setUnitName] = useState(defaultUnitName);
  const [boxLabel, setBoxLabel] = useState("Box 1");
  const [msg, setMsg] = useState<string | null>(null);

  const unitOptions = useMemo(() => {
    const units = listMedicalUnits().filter((u) => u.active);
    if (serviceId === "clinical") {
      return units
        .filter((u) => !["Laboratoire", "Imagerie", "Pharmacie"].includes(u.name))
        .map((u) => u.name);
    }
    if (serviceId === "pharmacy") return ["Pharmacie"];
    if (serviceId === "laboratory") return ["Laboratoire"];
    if (serviceId === "imaging") return ["Imagerie"];
    if (serviceId === "nursing") {
      return ["Prise des constantes", "Hospitalisation", "Tournée"];
    }
    if (serviceId === "admission") return ["Accueil", "Médecine générale", "Consultation externe"];
    return units.map((u) => u.name);
  }, [serviceId]);

  const boxes = useMemo(() => {
    const unit = listMedicalUnits().find((u) => u.name === unitName);
    return unit?.boxes?.length ? unit.boxes : DEFAULT_BOXES;
  }, [unitName]);

  useEffect(() => {
    if (!unitName && unitOptions[0]) setUnitName(unitOptions[0]);
  }, [unitName, unitOptions]);

  useEffect(() => {
    if (boxes[0] && !boxes.includes(boxLabel)) setBoxLabel(boxes[0]);
  }, [boxes, boxLabel]);

  useEffect(() => {
    const refresh = () => setTick((n) => n + 1);
    window.addEventListener("hospitol:queue", refresh);
    return () => window.removeEventListener("hospitol:queue", refresh);
  }, []);

  const tickets = useMemo(() => {
    void tick;
    const list = listQueueTickets({
      serviceId,
      unitName: unitName || undefined,
      status: ["waiting", "called", "in_progress"],
    });
    return ticketFilter ? list.filter(ticketFilter) : list;
  }, [tick, serviceId, unitName, ticketFilter]);

  const pagination = useClientPagination(tickets, [unitName], 15);
  const { pagedItems: pagedTickets } = pagination;

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  const handleCallNext = () => {
    if (!user || !unitName) return;
    const name =
      user.displayName ??
      [user.firstName, user.lastName].filter(Boolean).join(" ") ??
      user.email ??
      "Praticien";
    const called = callNextTicket({
      serviceId,
      unitName,
      boxLabel,
      practitionerId: user.id,
      practitionerName: name,
    });
    if (!called) {
      setMsg("Aucun patient en attente.");
      return;
    }
    setMsg(`${patientFullName(getPatientById(called.patientId) ?? { firstName: "?", lastName: "" })} appelé — ${boxLabel}`);
    refresh();
  };

  const handleStart = (ticket: QueueTicket) => {
    if (serviceId === "clinical") {
      const consultation = createConsultation({
        patientId: ticket.patientId,
        motif: ticket.motif,
      });
      startQueueTicket(ticket.id, consultation.id);
      navigate(consultationDetailPath(consultation.id));
      return;
    }
    if (serviceId === "pharmacy") {
      const rx = getPharmacyPrescriptionForPatient(ticket.patientId);
      startQueueTicket(ticket.id);
      if (rx) {
        navigate(pharmacyOrderPath(rx.id));
      } else {
        navigate(routes.pharmacyOrders);
      }
      return;
    }
    if (serviceId === "laboratory") {
      createLabOrder({
        patientId: ticket.patientId,
        testName: ticket.motif || "Analyse demandée",
        clinicalNotes: "Orientation accueil / file d'attente",
      });
      startQueueTicket(ticket.id);
      refresh();
      return;
    }
    if (serviceId === "imaging") {
      createImagingExam({
        patientId: ticket.patientId,
        examType: ticket.motif || "Examen demandé",
        bodyPart: "",
      });
      startQueueTicket(ticket.id);
      refresh();
      return;
    }
    if (serviceId === "admission") {
      orientPaidTicketToClinical(ticket.id);
      setMsg("Patient orienté vers la salle d'attente clinique (généraliste).");
      refresh();
      return;
    }
    if (serviceId === "nursing") {
      startQueueTicket(ticket.id);
      setMsg("Patient pris en charge — saisir les constantes dans le formulaire.");
      refresh();
      return;
    }
    startQueueTicket(ticket.id);
    refresh();
  };

  const handleComplete = (ticketId: string) => {
    completeQueueTicket(ticketId);
    refresh();
  };

  return (
    <div className="ph-queue-panel ph-invoice-sheet mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ph-split-detail-head">
        <h3 className="h6 fw-semibold mb-0">
          {title ?? `File d'attente — ${SERVICE_QUEUE_LABELS[serviceId]}`}
        </h3>
        <span className="badge bg-secondary">{tickets.length} en file</span>
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-sm-6 col-md-4">
          <label className="form-label small fw-semibold mb-1" htmlFor={`q-unit-${serviceId}`}>
            Unité / service
          </label>
          <select
            id={`q-unit-${serviceId}`}
            className="form-select form-select-sm"
            value={unitName}
            onChange={(e) => setUnitName(e.target.value)}
          >
            {unitOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        {enableCallNext && (
          <>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-semibold mb-1" htmlFor={`q-box-${serviceId}`}>
                Box / salle
              </label>
              <select
                id={`q-box-${serviceId}`}
                className="form-select form-select-sm"
                value={boxLabel}
                onChange={(e) => setBoxLabel(e.target.value)}
              >
                {boxes.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <button type="button" className="btn btn-primary btn-sm w-100" onClick={handleCallNext}>
                Appeler suivant
              </button>
            </div>
          </>
        )}
      </div>

      {msg && <p className="small text-success mb-2">{msg}</p>}

      {tickets.length === 0 ? (
        <p className="small text-muted mb-0">Salle d&apos;attente vide.</p>
      ) : (
        <div className="table-responsive ph-diag-table">
          <table className="table table-sm ph-dash-table align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Patient</th>
                <th>Motif</th>
                <th>Statut</th>
                <th>Box</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedTickets.map((ticket, index) => {
                const patient = getPatientById(ticket.patientId);
                const rowNum = (pagination.page - 1) * pagination.pageSize + index + 1;
                return (
                  <tr key={ticket.id} className="ph-diag-row">
                    <td className="text-muted small ph-diag-no-label" data-label="#">
                      {rowNum}
                    </td>
                    <td className="fw-medium" data-label="Patient">
                      {patient ? patientFullName(patient) : "—"}
                    </td>
                    <td className="small" data-label="Motif">
                      {ticket.motif || "—"}
                    </td>
                    <td data-label="Statut">
                      <span className={`hosp-clinical-badge hosp-clinical-badge--${ticket.status === "in_progress" ? "open" : "closed"}`}>
                        {QUEUE_STATUS_LABELS[ticket.status]}
                      </span>
                    </td>
                    <td className="small" data-label="Box">
                      {ticket.boxLabel ?? "—"}
                    </td>
                    <td className="text-end" data-label="Actions">
                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                        {ticket.status === "waiting" && serviceId === "admission" && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => handleStart(ticket)}
                          >
                            Envoyer en consultation
                          </button>
                        )}
                        {ticket.status === "called" && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleStart(ticket)}
                          >
                            {serviceId === "clinical" ? "Consultation" : "Prendre en charge"}
                          </button>
                        )}
                        {ticket.status === "in_progress" && serviceId === "clinical" && ticket.consultationId && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate(consultationDetailPath(ticket.consultationId!))}
                          >
                            Reprendre
                          </button>
                        )}
                        {(ticket.status === "in_progress" || ticket.status === "called") && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleComplete(ticket.id)}
                          >
                            Terminer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalItems}
            pageSizeOptions={[15, 25, 50]}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </div>
      )}
    </div>
  );
}
